import type { Plan } from '@/lib/subscription';
import { ROLE_META, formatPrice, formatStorage } from '@/lib/subscription';

interface PlanCardProps {
  plan: Plan;
  current?: boolean;
  actionLabel: string;
  onAction?: () => void;
  disabled?: boolean;
  loading?: boolean;
}

function PlanCard({ plan, current, actionLabel, onAction, disabled, loading }: PlanCardProps) {
  const meta = ROLE_META[plan.role] ?? { label: plan.role, icon: 'ri-vip-crown-line', postLabel: 'publications' };

  const features = [
    { icon: 'ri-file-list-3-line', label: `${plan.max_listings} ${meta.postLabel}` },
    { icon: 'ri-shopping-bag-3-line', label: `${plan.max_products} produits` },
    { icon: 'ri-hard-drive-2-line', label: `${formatStorage(plan.storage_mb)} de stockage` },
    { icon: 'ri-apps-2-line', label: plan.modules.map((m) => m).join(', ') },
  ];

  return (
    <div
      className={`relative p-6 rounded-xl border bg-background-100 transition-all duration-300 flex flex-col ${
        current ? 'border-accent-400/60 ring-2 ring-accent-400/30' : 'border-background-200/70 hover:border-accent-400/40'
      }`}
    >
      {current && (
        <span className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-accent-500 text-foreground-950 text-xs font-bold whitespace-nowrap">
          Offre actuelle
        </span>
      )}

      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/10">
          <i className={`${meta.icon} text-xl text-accent-400`} />
        </div>
        <h3 className="font-heading font-semibold text-lg text-foreground-50">{plan.name}</h3>
      </div>

      <div className="mb-4">
        <p className="font-heading font-bold text-3xl text-foreground-50">
          {formatPrice(plan.price_monthly)}
          <span className="text-sm font-normal text-foreground-600"> /mois</span>
        </p>
        {plan.description && (
          <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{plan.description}</p>
        )}
      </div>

      <ul className="space-y-2.5 mb-6 flex-1">
        {features.map((f) => (
          <li key={f.label} className="flex items-start gap-2.5 text-sm text-foreground-600">
            <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
              <i className={`${f.icon} text-primary-400 text-sm`} />
            </span>
            <span>{f.label}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onAction}
        disabled={disabled || loading || current}
        className={`w-full px-5 py-3 rounded-lg font-semibold text-sm transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed ${
          current
            ? 'bg-background-200/60 text-foreground-500'
            : 'bg-primary-500 text-foreground-950 hover:bg-primary-400'
        }`}
      >
        {loading ? 'Traitement…' : current ? 'Abonnement actif' : actionLabel}
      </button>
    </div>
  );
}

export default PlanCard;